import { useCallback, useEffect, useRef, type KeyboardEvent, type RefObject } from 'react';

/** Entries, and the options of a list nested in the menu (the organization list in the Settings menu). */
const ITEMS =
  '[role="menuitem"]:not([disabled]), [role="menuitemradio"]:not([disabled]), [role="listbox"] button:not([disabled])';

/** An entry the bar hides at a narrow or wide width (`c3-show-le*`) is not one the arrows can land on. */
function isShown(el: HTMLElement): boolean {
  const style = window.getComputedStyle(el);
  return style.display !== 'none' && style.visibility !== 'hidden';
}

/**
 * Arrow-key use of a menu (item 69), for the menus of the top bar. `useDismiss` already closes on
 * Escape and on a press outside; this adds the rest of the menu pattern:
 * - on the trigger, ArrowDown opens the menu on its first item and ArrowUp on its last;
 * - in the menu, ArrowDown and ArrowUp move between the items and wrap, Home and End go to the ends;
 * - Tab moves on as usual, and the menu closes when the focus has left it.
 * Put `onTriggerKeyDown` on the button that opens the menu and `onMenuKeyDown` on the menu's container.
 */
export function useMenuKeys(
  containerRef: RefObject<HTMLElement | null>,
  open: boolean,
  setOpen: (open: boolean) => void
) {
  const focusOnOpen = useRef<'first' | 'last' | null>(null);

  const items = useCallback(
    () => Array.from(containerRef.current?.querySelectorAll<HTMLElement>(ITEMS) ?? []).filter(isShown),
    [containerRef]
  );

  useEffect(() => {
    if (!open || !focusOnOpen.current) return;
    const list = items();
    (focusOnOpen.current === 'first' ? list[0] : list[list.length - 1])?.focus();
    focusOnOpen.current = null;
  }, [open, items]);

  // The focus moves on after the key handler has run, so look where it went. It may have gone to something
  // inside the menu (the organization list in the Settings menu is reached by Tab).
  const closeIfFocusLeft = useCallback(() => {
    window.setTimeout(() => {
      if (!containerRef.current?.contains(document.activeElement)) setOpen(false);
    }, 0);
  }, [containerRef, setOpen]);

  const onTriggerKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === 'Tab') {
        if (open) closeIfFocusLeft();
        return;
      }
      if (e.key !== 'ArrowDown' && e.key !== 'ArrowUp') return;
      e.preventDefault();
      focusOnOpen.current = e.key === 'ArrowDown' ? 'first' : 'last';
      if (open) {
        const list = items();
        (e.key === 'ArrowDown' ? list[0] : list[list.length - 1])?.focus();
        focusOnOpen.current = null;
      } else {
        setOpen(true);
      }
    },
    [open, items, setOpen, closeIfFocusLeft]
  );

  const onMenuKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === 'Tab') {
        closeIfFocusLeft();
        return;
      }
      if (!['ArrowDown', 'ArrowUp', 'Home', 'End'].includes(e.key)) return;
      const list = items();
      if (list.length === 0) return;
      const at = list.indexOf(document.activeElement as HTMLElement);
      // Focus on something else in the menu (a field, say) keeps its own arrow keys.
      if (at < 0 && document.activeElement !== containerRef.current) return;
      e.preventDefault();
      let next = at;
      if (e.key === 'ArrowDown') next = at < 0 ? 0 : (at + 1) % list.length;
      else if (e.key === 'ArrowUp') next = at < 0 ? list.length - 1 : (at - 1 + list.length) % list.length;
      else if (e.key === 'Home') next = 0;
      else next = list.length - 1;
      list[next].focus();
    },
    [items, closeIfFocusLeft, containerRef]
  );

  return { onTriggerKeyDown, onMenuKeyDown };
}
